/*! abi-uls-parts.js: behaviour of sections/abi-uls-parts.liquid (ULS parts collection: finder, model pages, part-type pages).
 * Theme asset, loaded with `defer` by the section only. Plain ES5, no dependencies, one IIFE. Feature uls-parts-collection, prefix upc.
 * Spec: review/uls-parts-collection-build-2026-10-05/ (DOM-CONTRACT.md section 5, BUILD-BRIEF.md, L/DOM-HOOKS.txt, J/CONTRACT-ADDITIONS.txt).
 *
 * 1. core: pure logic (map index, model and part-number matching, rows, groups, routes, money). Exported for the node tests
 *    (module.exports) and never touches window or document at require time. Nothing is put on window.
 * 2. DOM: one controller per [data-upc] root. Liquid renders D01 and the no-script fallbacks; this file fills the <template>s
 *    (textContent only), toggles `hidden`, moves existing nodes and writes history.
 *
 * Rules the code keeps: every printed word comes from the data-upc-config island (a missing string prints nothing, never a
 * default); OEM and ABI parts use one row template and one order (the map's); a part with no product in the products island is
 * not sold online and is left out; nothing is printed from a fallback (no store data, no parts list, no catalog: no claim).
 */
(function () {
  'use strict';

  /* ======================================================================================
   * 1. CORE (pure)
   * ==================================================================================== */
  var core = (function () {
    var ROWS_SHOWN = 4;

    function text(v) { return v == null ? '' : String(v); }
    function trim(v) { return text(v).replace(/^\s+|\s+$/g, ''); }
    function isObj(v) { return !!v && typeof v === 'object' && !Array.isArray(v); }
    function arr(v) { return Array.isArray(v) ? v : []; }
    function toInt(v) { var n = parseInt(v, 10); return isNaN(n) ? 0 : n; }
    function norm(t) { return text(t).toLowerCase().replace(/[^a-z0-9]/g, ''); }
    function has(o, k) { return Object.prototype.hasOwnProperty.call(o, k); }

    /* "{name}" placeholders. An unknown placeholder stays as written (the tests look for it). */
    function format(tpl, vars) {
      return text(tpl).replace(/\{(\w+)\}/g, function (m, k) { return vars && vars[k] != null ? String(vars[k]) : m; });
    }
    /* str(key, vars): the island's copy; a missing key prints nothing. */
    function makeStr(strings) {
      var src = isObj(strings) ? strings : {};
      function str(key, vars) { return typeof src[key] === 'string' ? format(src[key], vars) : ''; }
      str.has = function (key) { return typeof src[key] === 'string' && src[key] !== ''; };
      return str;
    }

    /* ---------- money (Shopify.formatMoney) ---------- */
    function decodeEntities(s) {
      return text(s).replace(/&#(\d+);/g, function (m, n) { return String.fromCharCode(parseInt(n, 10)); })
        .replace(/&quot;/g, '"').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&');
    }
    function delimit(cents, precision, thousands, decimal) {
      var n = Math.abs(Number(cents) || 0) / 100;
      var fixed = n.toFixed(precision);
      var parts = fixed.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return (cents < 0 ? '-' : '') + whole + (parts[1] ? decimal + parts[1] : '');
    }
    function formatMoney(cents, fmt) {
      var f = decodeEntities(fmt) || '${{amount}}';
      return f.replace(/\{\{\s*(\w+)\s*\}\}/g, function (m, k) {
        if (k === 'amount') return delimit(cents, 2, ',', '.');
        if (k === 'amount_no_decimals') return delimit(cents, 0, ',', '.');
        if (k === 'amount_with_comma_separator') return delimit(cents, 2, '.', ',');
        if (k === 'amount_no_decimals_with_comma_separator') return delimit(cents, 0, '.', ',');
        if (k === 'amount_with_apostrophe_separator') return delimit(cents, 2, '\'', '.');
        if (k === 'amount_no_decimals_with_space_separator') return delimit(cents, 0, ' ', '.');
        if (k === 'amount_with_space_separator') return delimit(cents, 2, ' ', ',');
        return m;
      });
    }

    /* ---------- titles ---------- */
    /* Display title (Charles, 2026-10-05): the store title minus the product's own part number where the title starts or ends with it.
       Port of mapping/display-title.cjs without a regex lookbehind (ES5): the lookbehind of the trailing bare part number is checked by hand.
       Removed: "SKU - Title", "SKU Title", "SKU: Title"; "Title (SKU)", "Title (part # SKU)", "Title SKU", "Title | SKU" (with their separators).
       Kept: a part number in the middle, any other part number, and the whole title when nothing else would be left. */
    var SEP = '[\\s' + String.fromCharCode(0x2013, 0x2014) + '\\-:|,]*';
    function displayTitle(title, sku) {
      var t = trim(text(title).replace(/\s+/g, ' '));
      var s = trim(sku);
      if (!s) return t;
      var e = s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      var out = t.replace(new RegExp('^' + e + '(?![A-Za-z0-9])' + SEP, 'i'), '');
      var m = new RegExp(SEP + '\\(\\s*(?:part\\s*(?:#|no\\.?|number)?\\s*)?' + e + '\\s*\\)\\s*$', 'i').exec(out);
      if (m) out = out.slice(0, m.index);
      else {
        m = new RegExp('(' + SEP + ')' + e + '\\s*$', 'i').exec(out);
        if (m) {
          var pos = m.index + m[1].length;
          if (m[1].length > 0 || pos === 0 || !/[A-Za-z0-9]/.test(out.charAt(pos - 1))) out = out.slice(0, m.index);
        }
      }
      out = trim(out);
      return out || t;
    }
    /* Brand line of a product that has no map entry: the vendor minus a trailing ", Inc." (a comma or a space must come before "Inc": "Zinc" stays).
       snippets/abi-uls-parts-brand.liquid is the same rule for the server-rendered rows and cards. */
    function vendorBrand(vendor) { return trim(trim(vendor).replace(/(?:, ?| )Inc\.?$/i, '')); }

    /* ---------- products island ---------- */
    function parseProducts(json) {
      var list = [];
      var bySku = {};
      arr(json).forEach(function (p) {
        if (!isObj(p)) return;
        var s = trim(p.s);
        var h = trim(p.h);
        var u = trim(p.u);
        if (!s || !h || !u) return;
        var key = s.toUpperCase();
        if (has(bySku, key)) return;
        var rec = {
          s: s, h: h, u: u, n: text(p.n), d: text(p.d), v: p.v, p: toInt(p.p),
          c: p.c == null || p.c === false ? null : toInt(p.c), a: p.a === true, i: p.i ? String(p.i) : '',
          m: toInt(p.m) || 1, x: p.x === true,
          /* fp: the lowest variant price (cents), present only when the variants differ in price */
          fp: p.fp == null || p.fp === false || toInt(p.fp) <= 0 ? null : toInt(p.fp)
        };
        bySku[key] = rec;
        list.push(rec);
      });
      return { list: list, bySku: bySku };
    }

    /* ---------- map index ---------- */
    function addKey(o, k, m) { if (!k) return; if (!has(o, k)) o[k] = []; if (o[k].indexOf(m) === -1) o[k].push(m); }
    function indexMap(json) {
      if (!isObj(json) || !Array.isArray(json.models) || !Array.isArray(json.parts) || !Array.isArray(json.families)) return null;
      var idx = {
        raw: json, groups: arr(json.groups), types: isObj(json.types) ? json.types : {}, families: json.families,
        models: json.models, parts: json.parts, popular: arr(json.popular),
        byId: {}, byName: {}, byAlias: {}, modelPos: {}, partBySku: {}, modelsOfPart: [], abiOf: {}, groupById: {}
      };
      idx.groups.forEach(function (g) { idx.groupById[g.id] = g; });
      json.parts.forEach(function (p, i) {
        idx.partBySku[text(p.s).toUpperCase()] = i;
        idx.modelsOfPart[i] = [];
        if (p.o) {
          var ok = text(p.o).toUpperCase();
          if (!has(idx.abiOf, ok)) idx.abiOf[ok] = [];
          idx.abiOf[ok].push(i);
        }
      });
      json.models.forEach(function (m, mi) {
        idx.byId[text(m.id).toLowerCase()] = m;
        idx.modelPos[m.id] = mi;
        addKey(idx.byName, norm(m.n), m);
        arr(m.a).forEach(function (a) { addKey(idx.byAlias, norm(a), m); });
        arr(m.p).forEach(function (pi) { if (idx.modelsOfPart[pi]) idx.modelsOfPart[pi].push(mi); });
      });
      return idx;
    }

    /* A model by id, then by name, then by alias (names and aliases compared without case, spaces and punctuation).
       An alias two models share (for example "Optima") resolves to nothing: the list shows both. */
    function resolveModel(idx, value) {
      if (!idx) return null;
      var v = trim(value);
      if (!v) return null;
      var byId = idx.byId[v.toLowerCase()];
      if (byId) return byId;
      var n = norm(v);
      if (!n) return null;
      var a = idx.byName[n];
      if (a) return a.length === 1 ? a[0] : null;
      var b = idx.byAlias[n];
      if (b && b.length === 1) return b[0];
      return null;
    }
    function rankOf(m, q) {
      var name = norm(m.n);
      var best = 99;
      function consider(k, exact, prefix, sub) {
        if (!k) return;
        if (k === q) best = Math.min(best, exact);
        else if (k.indexOf(q) === 0) best = Math.min(best, prefix);
        else if (k.indexOf(q) > 0) best = Math.min(best, sub);
      }
      consider(name, 0, 1, 4);
      arr(m.a).forEach(function (a) { consider(norm(a), 2, 3, 5); });
      consider(norm(m.id), 2, 3, 5);
      return best;
    }
    /* Models matching typed text (names, aliases, id), in family order (the list is grouped by family), best match first inside a family. */
    function searchModels(idx, query) {
      var q = norm(query);
      var out = [];
      idx.models.forEach(function (m, i) {
        var rank = q ? rankOf(m, q) : 0;
        if (rank < 99) out.push({ model: m, rank: rank, i: i });
      });
      out.sort(function (a, b) { return (a.model.f - b.model.f) || (a.rank - b.rank) || (a.i - b.i); });
      return out;
    }

    /* ---------- model results ---------- */
    /* The model's parts that have a product in the products island, in the map's display order. */
    function modelRows(idx, model, products) {
      var rows = [];
      arr(model.p).forEach(function (pi) {
        var part = idx.parts[pi];
        if (!part) return;
        var prod = products.bySku[text(part.s).toUpperCase()];
        if (!prod) return;
        rows.push({ part: part, partIdx: pi, prod: prod });
      });
      return rows;
    }
    /* Groups in map.groups order; inside a group, runs by part type (null type first runs are plain rows). kinds = distinct types. */
    function groupRows(idx, rows) {
      var out = [];
      idx.groups.forEach(function (g) {
        var rs = rows.filter(function (r) { return r.part.g === g.id; });
        if (!rs.length) return;
        var order = [];
        var byT = {};
        rs.forEach(function (r) {
          var t = r.part.t || '';
          if (!has(byT, t)) { byT[t] = []; order.push(t); }
          byT[t].push(r);
        });
        out.push({
          group: g, rows: rs, kinds: order.length,
          runs: order.map(function (t) { return { t: t, label: t ? text(idx.types[t] || t) : '', rows: byT[t] }; })
        });
      });
      return out;
    }
    function samePair(prev, cur) {
      if (!prev || !cur || !cur.part.o) return false;
      var o = text(cur.part.o).toUpperCase();
      return text(prev.part.s).toUpperCase() === o || (!!prev.part.o && text(prev.part.o).toUpperCase() === o);
    }
    /* How many rows of a list show before "Show n more": 4, and a part stays with its paired part (equal prominence). */
    function foldAt(rows, limit) {
      var n = limit || ROWS_SHOWN;
      if (rows.length <= n) return rows.length;
      while (n < rows.length && samePair(rows[n - 1], rows[n])) n += 1;
      return n;
    }
    /* "Fits these lasers" for a part: store-tagged models (kinds t and s) by family, then the parts-list models together. */
    function fitGroups(idx, partIdx, olderLabel) {
      var models = arr(idx.modelsOfPart[partIdx]).map(function (i) { return idx.models[i]; });
      var main = models.filter(function (m) { return m.k === 't' || m.k === 's'; });
      var older = models.filter(function (m) { return m.k === 'k'; });
      var groups = [];
      idx.families.forEach(function (fam, f) {
        var names = main.filter(function (m) { return m.f === f; }).map(function (m) { return m.n; });
        if (names.length) groups.push({ label: text(fam.l), names: names });
      });
      if (older.length) groups.push({ label: text(olderLabel), names: older.map(function (m) { return m.n; }), older: true });
      return { groups: groups, hasOlder: older.length > 0, count: models.length };
    }
    /* The equivalent SKUs of a part, either direction: the OEM part an ABI part replaces, and the ABI parts that replace an OEM part. */
    function equivalents(idx, sku) {
      var up = text(sku).toUpperCase();
      var out = [];
      var pi = idx.partBySku[up];
      if (pi != null && idx.parts[pi].o) out.push(text(idx.parts[pi].o));
      arr(idx.abiOf[up]).forEach(function (i) { out.push(text(idx.parts[i].s)); });
      return out;
    }

    /* ---------- part numbers ---------- */
    function pnUpper(s) { return text(s).toUpperCase().replace(/\s+/g, ''); }
    function pnKey(s) { return text(s).toUpperCase().replace(/[^A-Z0-9]/g, ''); }
    /* the part number without its revision suffix: "145-0024-00-A" -> "145002400" (short last segment, at least three segments) */
    function baseKey(pn) {
      var segs = trim(pn).split('-');
      if (segs.length < 3) return '';
      var last = segs[segs.length - 1];
      if (!last || last.length > 2) return '';
      return pnKey(segs.slice(0, -1).join('-'));
    }
    function buildPnIndex(list, getPn) {
      var idx = { exact: {}, key: {}, base: {} };
      function push(o, k, e) { if (!k) return; if (!has(o, k)) o[k] = []; o[k].push(e); }
      arr(list).forEach(function (e) {
        var pn = getPn(e);
        push(idx.exact, pnUpper(pn), e);
        push(idx.key, pnKey(pn), e);
        push(idx.base, baseKey(pn), e);
      });
      return idx;
    }
    /* exact (spaces and case ignored), then without dashes, then without the revision suffix: the last two only when exactly one part matches */
    function lookupPn(pnIdx, typed) {
      var q = pnUpper(typed);
      if (!q) return null;
      if (has(pnIdx.exact, q)) return { entry: pnIdx.exact[q][0], level: 1 };
      var k = pnKey(typed);
      if (!k) return null;
      if (has(pnIdx.key, k)) return pnIdx.key[k].length === 1 ? { entry: pnIdx.key[k][0], level: 2 } : null;
      if (has(pnIdx.base, k) && pnIdx.base[k].length === 1) return { entry: pnIdx.base[k][0], level: 3 };
      return null;
    }
    /* "On Universal's parts lists for": the catalog part's model names, grouped by catalog series. */
    function catalogFits(catalog, part) {
      var models = {};
      arr(catalog && catalog.models).forEach(function (m) { models[m.id] = m; });
      var seriesName = {};
      var seriesOrder = [];
      arr(catalog && catalog.series).forEach(function (s) { seriesName[s.id] = s.name; seriesOrder.push(s.id); });
      var by = {};
      arr(part && part.fits).forEach(function (id) {
        var m = models[id];
        if (!m) return;
        if (!has(by, m.series)) by[m.series] = [];
        by[m.series].push(text(m.name));
      });
      var out = [];
      seriesOrder.forEach(function (sid) { if (has(by, sid)) out.push({ label: text(seriesName[sid]), names: by[sid] }); });
      Object.keys(by).forEach(function (sid) { if (seriesOrder.indexOf(sid) === -1) out.push({ label: text(sid), names: by[sid] }); });
      return out;
    }

    /* ---------- routes ---------- */
    function decodePart(s) {
      try { return decodeURIComponent(text(s).replace(/\+/g, ' ')); } catch (e) { return text(s); }
    }
    function parseQuery(search) {
      var o = Object.create(null);
      text(search).replace(/^\?/, '').split('&').forEach(function (pr) {
        if (!pr) return;
        var at = pr.indexOf('=');
        var k = decodePart(at === -1 ? pr : pr.slice(0, at));
        var v = at === -1 ? '' : decodePart(pr.slice(at + 1));
        if (!(k in o)) o[k] = v;
      });
      return o;
    }
    function parseRoute(search) {
      var q = parseQuery(search);
      var model = trim(q.model);
      var pn = trim(q.pn);
      var type = trim(q.type);
      if (model) return { kind: 'model', model: model, type: type };
      if (pn) return { kind: 'pn', pn: pn };
      return { kind: 'default' };
    }
    /* The search string with the state keys (model, pn, type, page) replaced by the route's; other pairs are kept byte for byte. */
    function routeSearch(search, route) {
      var pairs = text(search).replace(/^\?/, '').split('&').filter(Boolean).filter(function (pr) {
        var at = pr.indexOf('=');
        var k = decodePart(at === -1 ? pr : pr.slice(0, at));
        return k !== 'model' && k !== 'pn' && k !== 'type' && k !== 'page';
      });
      if (route && route.kind === 'model' && route.model) pairs.push('model=' + encodeURIComponent(route.model));
      else if (route && route.kind === 'pn' && route.pn) pairs.push('pn=' + encodeURIComponent(route.pn));
      return pairs.length ? '?' + pairs.join('&') : '';
    }
    /* ?page=N (N > 1) pages the products island to empty: the same URL without any page pair, or null when there is nothing to fix. */
    function pageFixTarget(pathname, search, hash) {
      var pairs = text(search).replace(/^\?/, '').split('&').filter(Boolean);
      function isPage(pr) { var at = pr.indexOf('='); return decodePart(at === -1 ? pr : pr.slice(0, at)) === 'page'; }
      var pages = pairs.filter(isPage);
      if (!pages.length) return null;
      var harmful = pages.some(function (pr) { var at = pr.indexOf('='); return toInt(at === -1 ? '' : decodePart(pr.slice(at + 1))) > 1; });
      if (!harmful) return null;
      var keep = pairs.filter(function (pr) { return !isPage(pr); });
      return text(pathname) + (keep.length ? '?' + keep.join('&') : '') + text(hash);
    }
    function joinParams(base, params) {
      var b = trim(base);
      return b + (b.indexOf('?') === -1 ? '?' : '&') + params.join('&');
    }
    /* Contact link for topic=parts. The contact page (sections/abi-contact.liquid, readPdp) reads only: model (free text, 60 characters), from (must match
       ^/products/[a-z0-9-]+$, 200 characters at most), sku (must equal a variant SKU of that product), qty, reason. So: topic first, then model when a model is
       known, then from=/products/<handle> and sku=<SKU> only for a link about ONE product sold online (a handle the page would reject is left out together with the
       SKU). Never pn, never a collection URL: the page drops both. p = { model, handle, sku }. */
    var PRODUCT_HANDLE = /^[a-z0-9-]+$/;
    function productFrom(handle) {
      var h = trim(handle);
      var from = '/products/' + h;
      return h && PRODUCT_HANDLE.test(h) && from.length <= 200 ? from : '';
    }
    function contactHref(base, topic, p) {
      var q = ['topic=' + encodeURIComponent(topic || 'parts')];
      var model = p ? trim(p.model) : '';
      if (model) q.push('model=' + encodeURIComponent(model));
      var from = p ? productFrom(p.handle) : '';
      var sku = p ? trim(p.sku) : '';
      if (from && sku) { q.push('from=' + encodeURIComponent(from)); q.push('sku=' + encodeURIComponent(sku)); }
      return joinParams(base || '/pages/contact', q);
    }
    /* "See all models it fits": the part-number result of the finder, which lists every model */
    function fitsHref(finderUrl, sku) { return trim(finderUrl) + '?pn=' + encodeURIComponent(trim(sku)); }
    /* price line of a product: "From $x" when its variants differ in price, else the price */
    function priceText(prod, money, fromTpl) {
      if (prod.fp != null && prod.fp > 0 && fromTpl) return format(fromTpl, { price: money(prod.fp) });
      return money(prod.p);
    }
    function quoteHref(base, pn) {
      var b = trim(base);
      return pn ? joinParams(b, ['pn=' + encodeURIComponent(pn)]) : b;
    }
    function clampQty(v) { var n = parseInt(v, 10); if (isNaN(n) || n < 1) return 1; return n > 99 ? 99 : n; }

    return {
      ROWS_SHOWN: ROWS_SHOWN, text: text, trim: trim, norm: norm, format: format, makeStr: makeStr, formatMoney: formatMoney, displayTitle: displayTitle,
      vendorBrand: vendorBrand, parseProducts: parseProducts, indexMap: indexMap, resolveModel: resolveModel, searchModels: searchModels,
      modelRows: modelRows, groupRows: groupRows, foldAt: foldAt, fitGroups: fitGroups, equivalents: equivalents, pnUpper: pnUpper,
      pnKey: pnKey, baseKey: baseKey, buildPnIndex: buildPnIndex, lookupPn: lookupPn, catalogFits: catalogFits, parseQuery: parseQuery,
      parseRoute: parseRoute, routeSearch: routeSearch, pageFixTarget: pageFixTarget, contactHref: contactHref, productFrom: productFrom, fitsHref: fitsHref,
      priceText: priceText, quoteHref: quoteHref, clampQty: clampQty
    };
  })();

  if (typeof module === 'object' && module && module.exports) module.exports = core;
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  /* ======================================================================================
   * 2. DOM
   * ==================================================================================== */
  var CART_TIMEOUT = 15000;
  var CATALOG_TIMEOUT = 8000;
  var PAGE_FIX_KEY = 'upc:page-fix';
  var cart = { busy: false, chain: null };
  var boxSeq = 0;

  function qs(el, sel) { return el ? el.querySelector(sel) : null; }
  function qsa(el, sel) { return el ? Array.prototype.slice.call(el.querySelectorAll(sel)) : []; }
  function matches(el, sel) {
    var f = el.matches || el.msMatchesSelector || el.webkitMatchesSelector;
    return f ? f.call(el, sel) : false;
  }
  function closest(el, sel) {
    while (el && el.nodeType === 1) { if (matches(el, sel)) return el; el = el.parentNode; }
    return null;
  }
  function setHidden(el, hide) {
    if (!el) return;
    if (hide) { if (!el.hasAttribute('hidden')) el.setAttribute('hidden', ''); }
    else if (el.hasAttribute('hidden')) el.removeAttribute('hidden');
  }
  function setText(el, t) { if (el) el.textContent = t == null ? '' : String(t); }
  function setAttr(el, name, value) {
    if (!el) return;
    if (value == null || value === false) el.removeAttribute(name);
    else el.setAttribute(name, value === true ? '' : String(value));
  }
  function readJson(el) {
    if (!el) return null;
    try { return JSON.parse(el.textContent || 'null'); } catch (e) { return null; }
  }
  function slotOf(scope, name) {
    var sel = '[data-upc-slot="' + name + '"]';
    return scope && matches(scope, sel) ? scope : qs(scope, sel);
  }
  function fillSlot(scope, name, t) { var el = slotOf(scope, name); setText(el, t); return el; }
  function removeNode(el) { if (el && el.parentNode) el.parentNode.removeChild(el); }
  function clearNode(el) { while (el && el.firstChild) el.removeChild(el.firstChild); }
  function mq(query) { try { return window.matchMedia ? window.matchMedia(query) : null; } catch (e) { return null; } }
  var reduceMq = mq('(prefers-reduced-motion: reduce)');
  function reducedMotion() { return !!(reduceMq && reduceMq.matches); }
  function raf(fn) {
    if (typeof window.requestAnimationFrame === 'function') window.requestAnimationFrame(fn);
    else setTimeout(fn, 16);
  }
  function scrollY() { return window.pageYOffset || (document.documentElement && document.documentElement.scrollTop) || 0; }
  function focusEl(el) {
    if (!el) return;
    try { el.focus({ preventScroll: true }); } catch (e) { try { el.focus(); } catch (e2) { /* not focusable */ } }
  }
  function focusable(el) {
    if (!el) return null;
    if (!/^(A|BUTTON|INPUT|SELECT|TEXTAREA|SUMMARY)$/.test(el.tagName) && !el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
    return el;
  }
  function sessionStore() { try { return window.sessionStorage; } catch (e) { return null; } }
  function sstGet(k) { try { var s = sessionStore(); return s ? s.getItem(k) : null; } catch (e) { return null; } }
  function sstSet(k, v) { try { var s = sessionStore(); if (s) s.setItem(k, v); } catch (e) { /* blocked */ } }
  function sstDel(k) { try { var s = sessionStore(); if (s) s.removeItem(k); } catch (e) { /* blocked */ } }

  /* ---------- Webflow's anchor scroll ----------
     The theme loads the Webflow runtime (assets/h1-custom.js). Its jQuery handler on document ("click.wf-scroll") takes every
     same-page #id link, ignores preventDefault and scrolls with no header offset. Remove that one handler: now, when Webflow
     is ready, on DOMContentLoaded and on load. (Port of disableWebflowAnchorScroll() in uls-part-pdp.js.) */
  function disableWebflowAnchorScroll() {
    var off = function () {
      try { if (window.jQuery) window.jQuery(document).off('click.wf-scroll'); } catch (e) { /* no jQuery */ }
    };
    off();
    try { if (window.Webflow && typeof window.Webflow.push === 'function') window.Webflow.push(off); } catch (e) { /* no Webflow */ }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { setTimeout(off, 0); });
    window.addEventListener('load', off);
  }

  function tplNode(ctx, name) {
    var t = qs(ctx.root, 'template[data-upc-tpl="' + name + '"]');
    var src = t ? (t.content ? t.content.firstElementChild : t.firstElementChild) : null;
    return src ? document.importNode(src, true) : null;
  }
  /* a template's inner element when its .upc-wrap would sit inside a wrap that already pads the page */
  function tplInner(ctx, name) {
    var n = tplNode(ctx, name);
    if (n && n.classList && n.classList.contains('upc-wrap') && n.children.length === 1) return n.firstElementChild;
    return n;
  }

  /* ---------- the controller ---------- */
  /* Boot never leaves the page worse than the server made it: an exception anywhere in boot (init or the first state) clears data-upc-state (the pre-script's
     skeleton), marks the root data-upc-js="error" and puts the server-rendered view back: D01 on the finder, the rows of a model page, the cards of a browse page. */
  function initRoot(root) {
    if (!root || root.__upc) return;
    try { initRootUnsafe(root); } catch (err) { recoverRoot(root); }
  }
  function recoverRoot(root) {
    var ctx = root.__upc;
    try { if (ctx) restoreServerView(ctx); } catch (e) { /* the attributes below are what matters */ }
    try { root.setAttribute('data-upc-js', 'error'); root.setAttribute('data-upc-state', ''); } catch (e2) { /* detached */ }
  }
  function restoreServerView(ctx) {
    var root = ctx.root;
    if (ctx.mode === 'finder') {
      var v = views(ctx);
      if (v.res) { clearNode(v.res); setHidden(v.res, true); }
      if (v.pn) { clearNode(v.pn); v.pn.removeAttribute('aria-busy'); setHidden(v.pn, true); }
      setHidden(v.def, false);
      setHidden(qs(root, '[data-upc-notice]'), true);
      setCrumb(ctx, '');
    } else if (ctx.mode === 'model') {
      var host = qs(root, '[data-upc-view="results"]');
      if (host && ctx.fallbackEl && !ctx.fallbackEl.parentNode) host.appendChild(ctx.fallbackEl);
    } else if (ctx.mode === 'browse' && ctx.b) {
      removeBrowseNotes(ctx);
      if (ctx.b.model) resetBrowse(ctx);
      setHidden(ctx.b.idle, false);
      setHidden(ctx.b.applied, true);
    }
  }
  function initRootUnsafe(root) {
    var cfg = readJson(qs(root, 'script[data-upc-config]'));
    if (!cfg || typeof cfg !== 'object') cfg = {};
    var ctx = {
      root: root, cfg: cfg, str: core.makeStr(cfg.strings),
      mode: root.getAttribute('data-upc-mode') || cfg.mode || 'finder',
      index: core.indexMap(readJson(qs(root, 'script[data-upc-map]'))),
      products: core.parseProducts(readJson(qs(root, 'script[data-upc-products]'))),
      moneyFormat: root.getAttribute('data-upc-money-format') || '',
      finderUrl: root.getAttribute('data-upc-finder-url') || cfg.finder_url || '/',
      contactUrl: root.getAttribute('data-upc-contact-url') || cfg.contact_url || '/pages/contact',
      quoteUrl: root.getAttribute('data-upc-quote-url') || cfg.quote_url || '',
      catalogUrl: root.getAttribute('data-upc-catalog-url') || cfg.catalog_url || '',
      storageKey: cfg.storage_key || 'upc:model',
      headerH: 0, token: 0, view: 'default', model: null, pn: null, catalog: { status: 'idle', data: null, pnIdx: null, promise: null },
      boxes: [], spy: null, applied: null
    };
    root.__upc = ctx;
    root.setAttribute('data-upc-js', 'on');
    ctx.noData = !ctx.index || ctx.products.list.length === 0;
    ctx.money = function (cents) { return core.formatMoney(cents, ctx.moneyFormat); };
    ctx.announce = function (msg) {
      var region = qs(root, '[data-upc-live]');
      if (!region || !msg) return;
      region.textContent = '';
      setTimeout(function () { region.textContent = msg; }, 40);
    };

    disableWebflowAnchorScroll();
    initMeasure(ctx);
    initPlaceholders(ctx);
    if (!ctx.index) { root.setAttribute('data-upc-state', ''); return; }
    qsa(root, '[data-upc-combo]').forEach(function (box) { initCombobox(ctx, box); });
    initEvents(ctx);
    initScrollSpy(ctx);

    /* ?page=N (N > 1) pages the products island to empty: reload without it (once). */
    var target = core.pageFixTarget(window.location.pathname, window.location.search, window.location.hash);
    if (target && !recentPageFix(target)) {
      root.setAttribute('data-upc-redirect', target);
      markPageFix(target);
      go(ctx, target, true);
      setTimeout(function () { if (!ctx.started && root.isConnected) { try { start(ctx); } catch (err) { recoverRoot(root); } } }, 1500);
      return;
    }
    start(ctx);
  }

  function start(ctx) {
    if (ctx.started) return;
    ctx.started = true;
    if (ctx.mode === 'model') startModelPage(ctx);
    else if (ctx.mode === 'browse') startBrowse(ctx);
    else startFinder(ctx);
    ctx.root.setAttribute('data-upc-state', '');
  }

  function recentPageFix(target) {
    try {
      var o = JSON.parse(sstGet(PAGE_FIX_KEY) || 'null');
      return !!(o && o.t === target && Date.now() - o.at < 10000);
    } catch (e) { return false; }
  }
  function markPageFix(target) { sstSet(PAGE_FIX_KEY, JSON.stringify({ t: target, at: Date.now() })); }

  /* Navigation seam: a cancelable document-level event first (the tests use it), then the real navigation. */
  function go(ctx, href, replace) {
    var ev = null;
    try { ev = new window.CustomEvent('upc:navigate', { bubbles: true, cancelable: true, detail: { href: href, replace: !!replace } }); }
    catch (e) { ev = null; }
    if (ev && !ctx.root.dispatchEvent(ev)) return;
    try { if (replace) window.location.replace(href); else window.location.assign(href); } catch (e2) { /* sandboxed */ }
  }

  /* ---------- header offset: --upc-header-h on the root (the V3 header compacts on scroll) ---------- */
  function initMeasure(ctx) {
    var root = ctx.root;
    var sel = root.getAttribute('data-upc-header-selector') || ctx.cfg.header_selector || '.abh-section';
    var ro = typeof window.ResizeObserver === 'function' ? new window.ResizeObserver(function () { schedule(); }) : null;
    var observed = null;
    var queued = false;
    function findHeader() {
      var h = null;
      try { h = document.querySelector(sel); } catch (e) { h = null; }
      return h || document.querySelector('[data-abh]') || document.querySelector('header');
    }
    function stickyNode(node) {
      for (var n = node; n && n !== document.documentElement && n !== document.body; n = n.parentElement) {
        var p = '';
        try { p = window.getComputedStyle(n).position; } catch (e) { p = ''; }
        if (p === 'sticky' || p === 'fixed') return n;
      }
      return null;
    }
    function measure() {
      var header = findHeader();
      var s = header ? stickyNode(header) || header : null;
      var h = s ? Math.max(0, Math.round(s.getBoundingClientRect().height)) : 0;
      if (h !== ctx.headerH || !root.style.getPropertyValue('--upc-header-h')) {
        ctx.headerH = h;
        root.style.setProperty('--upc-header-h', h + 'px');
      }
      if (ro && s !== observed) {
        if (observed) ro.unobserve(observed);
        if (s) ro.observe(s);
        observed = s;
      }
    }
    function schedule() {
      if (queued) return;
      queued = true;
      raf(function () { queued = false; measure(); });
    }
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    window.addEventListener('load', schedule);
    ctx.measure = measure;
    measure();
  }
  function stickyBarH(ctx) {
    var bar = qs(ctx.root, '.upc-bar');
    return bar && bar.getBoundingClientRect ? Math.round(bar.getBoundingClientRect().height) : 0;
  }
  function scrollToEl(ctx, el, extra, instant) {
    if (!el || !el.getBoundingClientRect) return;
    if (ctx.measure) ctx.measure();
    var top = Math.max(0, Math.round(el.getBoundingClientRect().top + scrollY() - (ctx.headerH || 0) - (extra || 0) - 16));
    try { window.scrollTo({ top: top, behavior: instant || reducedMotion() ? 'auto' : 'smooth' }); }
    catch (e) { try { window.scrollTo(0, top); } catch (e2) { /* jsdom */ } }
  }
  /* bring a heading into view below the sticky header when it is not */
  function revealEl(ctx, el) {
    if (!el || !el.getBoundingClientRect) return;
    var r = el.getBoundingClientRect();
    var vh = window.innerHeight || 0;
    if (!r.height && !r.top && !r.bottom) return; /* not laid out (jsdom) */
    if (r.top < (ctx.headerH || 0) || r.bottom > vh) scrollToEl(ctx, el, 0, true);
  }

  /* data-upc-placeholders="long|medium|short": >= 1024 px, 360 to 1023 px, < 360 px */
  function initPlaceholders(ctx) {
    var inputs = qsa(ctx.root, '[data-upc-placeholders]');
    if (!inputs.length) return;
    var wide = mq('(min-width: 1024px)');
    var tiny = mq('(max-width: 359.98px)');
    function apply() {
      var k = wide && wide.matches ? 0 : (tiny && tiny.matches ? 2 : 1);
      inputs.forEach(function (i) {
        var parts = String(i.getAttribute('data-upc-placeholders') || '').split('|');
        var v = parts[k] || parts[1] || parts[0];
        if (v && i.getAttribute('placeholder') !== v) i.setAttribute('placeholder', v);
      });
    }
    [wide, tiny].forEach(function (m) {
      if (!m) return;
      if (m.addEventListener) m.addEventListener('change', apply); else if (m.addListener) m.addListener(apply);
    });
    apply();
  }

  /* ---------- contact and quote links ---------- */
  /* The script remembers what a contact link was built for (model, product handle, SKU) so it can rebuild it when the page's state changes.
     A link the server rendered (Liquid) is adopted first: its model / from / sku are read back from its href. The rules for what a link may carry
     are core.contactHref's (what the contact page reads: model, from=/products/<handle>, sku). */
  function adoptContact(a) {
    if (a.hasAttribute('data-upc-adopted')) return;
    a.setAttribute('data-upc-adopted', '');
    var href = a.getAttribute('href') || '';
    var at = href.indexOf('?');
    var q = core.parseQuery(at < 0 ? '' : href.slice(at));
    var handle = /^\/products\/([a-z0-9-]+)$/.exec(q.from || '');
    if (!a.hasAttribute('data-upc-model-name') && q.model) a.setAttribute('data-upc-model-name', q.model);
    if (!a.hasAttribute('data-upc-handle') && handle && q.sku) { a.setAttribute('data-upc-handle', handle[1]); a.setAttribute('data-upc-sku', q.sku); }
  }
  function setContact(ctx, a, p) {
    if (!a) return;
    adoptContact(a);
    if (p && p.model !== undefined) setAttr(a, 'data-upc-model-name', p.model || null);
    if (p && p.handle !== undefined) setAttr(a, 'data-upc-handle', p.handle || null);
    if (p && p.sku !== undefined) setAttr(a, 'data-upc-sku', p.sku || null);
    a.setAttribute('href', core.contactHref(ctx.contactUrl, ctx.cfg.contact_topic || 'parts', {
      model: a.getAttribute('data-upc-model-name') || '', handle: a.getAttribute('data-upc-handle') || '', sku: a.getAttribute('data-upc-sku') || ''
    }));
  }
  /* a heading of another level (the template holds the deepest one); keeps the attributes, the class and the children */
  function retag(el, tag) {
    if (!el || !el.parentNode || el.tagName.toLowerCase() === tag) return el;
    var n = document.createElement(tag);
    for (var i = 0; i < el.attributes.length; i++) n.setAttribute(el.attributes[i].name, el.attributes[i].value);
    while (el.firstChild) n.appendChild(el.firstChild);
    el.parentNode.replaceChild(n, el);
    return n;
  }
  function setQuote(ctx, a, pn) { if (a) a.setAttribute('href', core.quoteHref(ctx.quoteUrl, pn)); }

  /* ---------- rows ---------- */
  function brandFor(ctx, part, prod) {
    if (part) return part.b === 'abi' ? ctx.str('brand_abi') : ctx.str('brand_oem');
    return core.vendorBrand(prod.d);
  }
  /* opts: model (name for "Fits {model}"), details (part-number result: "Product details" -> the product page), ref (the other part number of an equivalent pair),
     level (heading level of the title: 4 in a sub-group, the template's own; callers pass the level that follows their section heading) */
  function buildRow(ctx, part, prod, opts) {
    opts = opts || {};
    var str = ctx.str;
    var el = tplNode(ctx, prod.x ? 'row-exchange' : 'row');
    if (!el) return null;
    var title = core.displayTitle(prod.n, prod.s);
    el.setAttribute('data-sku', prod.s);
    var imgLink = slotOf(el, 'img-link');
    if (imgLink) imgLink.setAttribute('href', prod.u);
    var img = slotOf(el, 'img');
    if (img) { if (prod.i) img.setAttribute('src', prod.i); else removeNode(img); }
    fillSlot(el, 'brand', brandFor(ctx, part, prod));
    var t = fillSlot(el, 'title', title);
    if (t) t.setAttribute('href', prod.u);
    fillSlot(el, 'pn', prod.s);
    var fit = slotOf(el, 'fit');
    if (fit) {
      if (opts.model) fillSlot(fit, 'fit-text', str('fits', { model: opts.model }));
      else removeNode(fit);
    }
    var ml = slotOf(el, 'models-link');
    if (ml) {
      if (opts.details) { ml.setAttribute('href', prod.u); setText(ml, str('details_link')); }
      else {
        /* "See all models it fits": the part-number result of the finder (every model the part is listed for); in-page on the finder, a normal link elsewhere */
        ml.setAttribute('href', core.fitsHref(ctx.finderUrl, prod.s));
        ml.setAttribute('data-upc-fits-link', prod.s);
        setText(ml, str('models_link'));
      }
    }
    var ref = slotOf(el, 'ref');
    if (ref) {
      if (opts.ref) { setText(ref, str('pn_equiv', { pn: opts.ref })); setHidden(ref, false); } else removeNode(ref);
    }
    fillSlot(el, 'price', core.priceText(prod, ctx.money, str('from_price')));
    var note = slotOf(el, 'note');
    if (note) {
      if (prod.x && prod.c != null && prod.c > prod.p) { setText(note, str('list_price', { price: ctx.money(prod.c) })); setHidden(note, false); }
      else removeNode(note);
    }
    if (prod.x) {
      var ex = slotOf(el, 'exchange-link');
      if (ex) { ex.setAttribute('href', prod.u); setText(ex, str('view_exchange')); }
    } else {
      var which = prod.m > 1 ? 'options' : (prod.a ? 'add' : 'ask');
      qsa(el, '[data-upc-buy]').forEach(function (b) { if (b.getAttribute('data-upc-buy') !== which) removeNode(b); });
      var buy = qs(el, '[data-upc-buy="' + which + '"]');
      if (which === 'options' && buy) { buy.setAttribute('href', prod.u); setText(buy, str('choose_options')); }
      if (which === 'ask' && buy) { setText(buy, str('ask')); setContact(ctx, buy, { model: opts.model || '', handle: prod.h, sku: prod.s }); }
      if (which === 'add' && buy) {
        var q = qs(buy, '[data-upc-qty-input]');
        if (q) q.setAttribute('aria-label', str('qty_aria', { name: title }));
        setText(slotOf(buy, 'add-label'), str('add'));
      }
    }
    if (opts.level && opts.level !== 4) retag(qs(el, '.upc-row__title'), 'h' + opts.level);
    return el;
  }

  /* ---------- model results (D02) ---------- */
  function countLine(ctx, n, q, short) {
    var str = ctx.str;
    if (n < 1) return '';
    var head = short ? (n === 1 ? str('online_one_short') : str('online_many_short', { n: n })) : (n === 1 ? str('online_one') : str('online_many', { n: n }));
    return q > 0 ? head + str('count_join') + str('quote_more', { q: q }) : head;
  }
  function liveFor(ctx, model, n) {
    if (model.k === 'n') return model.x ? model.x : ctx.str('nolist_title', { model: model.n });
    if (n < 1) return ctx.str('live_none', { model: model.n });
    return n === 1 ? ctx.str('live_one', { model: model.n }) : ctx.str('live_many', { n: n, model: model.n });
  }
  function groupLabel(ctx, g) { return (ctx.cfg.chip_labels && ctx.cfg.chip_labels[g.id]) || g.n; }

  function buildMasthead(ctx, model, n) {
    var m = tplNode(ctx, 'masthead');
    if (!m) return null;
    var str = ctx.str;
    var fam = ctx.index.families[model.f] || {};
    var img = slotOf(m, 'img');
    if (img) { if (fam.i) img.setAttribute('src', fam.i); else { removeNode(img); setHidden(qs(m, '.upc-mast__img'), true); } }
    fillSlot(m, 'eyebrow', fam.l ? str('eyebrow_model', { family: fam.l }) : '');
    fillSlot(m, 'eyebrow-short', fam.l ? str('eyebrow_model_short', { family: fam.l }) : '');
    fillSlot(m, 'h1', str('h1_model', { model: model.n }));
    var q = model.q > 0 ? model.q : 0;
    var line = model.k === 'n' ? '' : countLine(ctx, n, q, false);
    var lineShort = model.k === 'n' ? '' : countLine(ctx, n, q, true);
    fillSlot(m, 'count', line);
    fillSlot(m, 'count-short', lineShort);
    if (!line) { var sub = qs(m, '.upc-mast__sub'); setHidden(sub, true); }
    return m;
  }

  function buildGroupSection(ctx, model, g) {
    var str = ctx.str;
    var el = tplNode(ctx, 'group');
    var section = qs(el, '[data-upc-group]');
    section.id = 'upc-g-' + g.group.id;
    section.setAttribute('data-group', g.group.id);
    fillSlot(el, 'title', g.group.n);
    var cart2 = g.group.id === 'cartridges';
    var n = g.rows.length;
    fillSlot(el, 'count', cart2 ? str(n === 1 ? 'group_cartridges_one' : 'group_cartridges_many', { n: n }) : str(n === 1 ? 'group_one' : 'group_many', { n: n }));
    var body = slotOf(el, 'body');
    if (cart2) { var notice = tplNode(ctx, 'notice-exchange'); if (notice) body.appendChild(notice); }
    var sub = g.kinds > 1;
    g.runs.forEach(function (run) {
      var host = null;
      var label = '';
      var level = 3;
      if (sub && run.t) {
        level = 4;
        var sg = tplNode(ctx, 'subgroup');
        fillSlot(sg, 'title', run.label);
        fillSlot(sg, 'count', String(run.rows.length));
        body.appendChild(sg);
        host = qs(sg, '[data-upc-rows]');
        label = run.label.toLowerCase();
      } else {
        host = tplNode(ctx, 'rows');
        body.appendChild(host);
        label = cart2 ? str('more_label_cartridges') : str('more_default_label');
      }
      fillRows(ctx, model, host, run.rows, label, level);
    });
    return el;
  }
  /* a list shows 4 rows (a pair stays together) and "Show n more {label}" for the rest */
  function fillRows(ctx, model, host, rows, label, level) {
    var shown = core.foldAt(rows);
    rows.slice(0, shown).forEach(function (r) { appendRow(ctx, model, host, r, null, level); });
    var rest = rows.slice(shown);
    if (!rest.length) return;
    var more = tplNode(ctx, 'more');
    setText(slotOf(more, 'label'), ctx.str('more', { n: rest.length, label: label }));
    more.__rest = rest;
    more.__model = model;
    more.__level = level;
    host.appendChild(more);
  }
  function appendRow(ctx, model, host, r, before, level) {
    var el = buildRow(ctx, r.part, r.prod, { model: model ? model.n : '', level: level });
    if (!el) return null;
    if (before) host.insertBefore(el, before); else host.appendChild(el);
    return el;
  }
  function expandMore(ctx, moreRow) {
    var host = moreRow.parentNode;
    var model = moreRow.__model || ctx.model;
    var rest = moreRow.__rest || [];
    var first = null;
    rest.forEach(function (r) { var el = appendRow(ctx, model, host, r, moreRow, moreRow.__level); if (!first) first = el; });
    removeNode(moreRow);
    if (first) focusEl(focusable(qs(first, '.upc-row__title a') || first));
  }

  function buildBar(ctx, groups, total) {
    var bar = tplNode(ctx, 'bar');
    var chips = slotOf(bar, 'chips');
    function chip(label, count, href, current, gid) {
      var c = tplNode(ctx, 'bar-chip');
      fillSlot(c, 'label', label);
      fillSlot(c, 'count', String(count));
      c.setAttribute('href', href);
      c.setAttribute('data-group', gid);
      if (current) c.setAttribute('aria-current', 'location');
      chips.appendChild(c);
    }
    chip(ctx.str('chip_all'), total, '#', true, '');
    groups.forEach(function (g) { chip(groupLabel(ctx, g.group), g.rows.length, '#upc-g-' + g.group.id, false, g.group.id); });
    return bar;
  }

  /* The "no parts list" message of a kind n model: the registry's own note for the model (map x), printed as it is; the generic sentence and line only when x is missing */
  function fillNolist(ctx, nl, model) {
    if (model.x) {
      removeNode(slotOf(nl, 'title'));
      var tx = fillSlot(nl, 'text', model.x);
      if (tx) tx.classList.add('upc-quote__text--solo');
    } else {
      fillSlot(nl, 'title', ctx.str('nolist_title', { model: model.n }));
      fillSlot(nl, 'text', ctx.str('nolist_text'));
    }
    qsa(nl, '[data-upc-contact]').forEach(function (a) { setContact(ctx, a, { model: model.n, handle: '', sku: '' }); });
  }

  /* Build D02 for a model into `host` (a results view). Returns { h1, n }. The page is built off-line in a fragment and swapped in at the end, so an exception
     while building leaves whatever was in `host` (the server-rendered rows of a model page) untouched. */
  function renderModelInto(ctx, host, model, opts) {
    opts = opts || {};
    var rows = core.modelRows(ctx.index, model, ctx.products);
    var n = model.k === 'n' ? 0 : rows.length;
    var groups = core.groupRows(ctx.index, model.k === 'n' ? [] : rows);
    var frag = document.createDocumentFragment();

    var mast = buildMasthead(ctx, model, n);
    frag.appendChild(mast);
    if (model.k === 'k') { var kb = tplNode(ctx, 'kb-note'); fillSlot(kb, 'text', ctx.str('kb_note')); frag.appendChild(kb); }
    if (model.k === 'n') {
      var nl = tplNode(ctx, 'nolist');
      fillNolist(ctx, nl, model);
      frag.appendChild(nl);
    } else {
      if (n > 0) {
        frag.appendChild(buildBar(ctx, groups, n));
        groups.forEach(function (g) { frag.appendChild(buildGroupSection(ctx, model, g)); });
      }
      if (model.q > 0) {
        var qb = tplNode(ctx, 'quote-band');
        fillSlot(qb, 'title', ctx.str('quote_title', { q: model.q, model: model.n }));
        fillSlot(qb, 'text', ctx.str('quote_text'));
        qsa(qb, '[data-upc-quote-link]').forEach(function (a) { setQuote(ctx, a, ''); });
        qsa(qb, '[data-upc-contact]').forEach(function (a) { setContact(ctx, a, { model: model.n, handle: '', sku: '' }); });
        frag.appendChild(qb);
      } else {
        /* no quote count: a model result still ends with a route for the parts that are not listed */
        var rl = tplNode(ctx, 'route-line');
        qsa(rl, '[data-upc-quote-link]').forEach(function (a) { setQuote(ctx, a, ''); });
        qsa(rl, '[data-upc-contact]').forEach(function (a) { setContact(ctx, a, { model: model.n, handle: '', sku: '' }); });
        frag.appendChild(rl);
      }
    }
    frag.appendChild(tplNode(ctx, 'foot-results'));

    /* swap */
    var panel = qs(host, '[data-upc-change-panel]');
    if (panel) {
      panel.parentNode.removeChild(panel);
      frag.insertBefore(panel, mast.nextSibling);
    }
    ctx.fallbackEl = qs(host, '[data-upc-fallback]') || ctx.fallbackEl || null;
    clearNode(host);
    host.appendChild(frag);
    var spy = { groups: qsa(host, '[data-upc-group]'), chips: qsa(host, '.upc-bar [data-upc-chip]') };
    ctx.spy = spy.chips.length ? spy : null;
    return { h1: qs(host, '[data-upc-h1]'), n: n };
  }

  /* A category chip, or ?type=<group id>: mark the chip and scroll to the group (all parts: the first group). An unknown id does nothing. */
  function selectGroup(ctx, gid, instant) {
    var target = gid ? qs(ctx.root, '[data-upc-group][data-group="' + String(gid).replace(/[^\w-]/g, '') + '"]') : qs(ctx.root, '[data-upc-group]');
    if (!target) return;
    qsa(ctx.root, '.upc-bar [data-upc-chip]').forEach(function (c) {
      if ((c.getAttribute('data-group') || '') === (gid || '')) c.setAttribute('aria-current', 'location'); else c.removeAttribute('aria-current');
    });
    scrollToEl(ctx, target, stickyBarH(ctx), instant);
  }
  function initScrollSpy(ctx) {
    var queued = false;
    function update() {
      queued = false;
      var spy = ctx.spy;
      if (!spy || !spy.chips.length || !spy.chips[0].isConnected) return;
      var line = (ctx.headerH || 0) + stickyBarH(ctx) + 24;
      var cur = '';
      spy.groups.forEach(function (g) { if (g.getBoundingClientRect().top <= line) cur = g.getAttribute('data-group') || ''; });
      spy.chips.forEach(function (c) {
        if ((c.getAttribute('data-group') || '') === cur) c.setAttribute('aria-current', 'location'); else c.removeAttribute('aria-current');
      });
    }
    window.addEventListener('scroll', function () { if (!queued && ctx.spy) { queued = true; raf(update); } }, { passive: true });
  }

  /* ---------- finder ---------- */
  function views(ctx) {
    var r = ctx.root;
    return { def: qs(r, '[data-upc-view="default"]'), res: qs(r, '[data-upc-view="results"]'), pn: qs(r, '[data-upc-view="pn"]') };
  }
  function showView(ctx, name) {
    var v = views(ctx);
    ctx.view = name;
    setHidden(v.def, name !== 'default');
    setHidden(v.res, name !== 'results');
    setHidden(v.pn, name !== 'pn');
    if (name !== 'results' && v.res) clearNode(v.res);
    if (name !== 'pn' && v.pn) clearNode(v.pn);
    if (name !== 'results') ctx.spy = null;
  }
  /* breadcrumb: the last crumb shows the model or part number while a result is on screen */
  function setCrumb(ctx, t) {
    var li = qs(ctx.root, '[data-upc-crumb-last]');
    var span = slotOf(ctx.root, 'crumb-last');
    var link = qs(ctx.root, '[data-upc-crumb-finder]');
    if (ctx.mode !== 'finder') return;
    setHidden(li, !t);
    setText(span, t || '');
    setAttr(span, 'aria-current', t ? 'page' : null);
    setAttr(link, 'aria-current', t ? null : 'page');
  }

  /* The directory's "51 parts" counts are the mapping's (written when the data was generated); recompute them from the products island, which is the store as it is
     now. A model with none left online shows no count; a kind n row never had one and is left alone. No store data: the server numbers stay (nothing to compare with). */
  function refreshDirectory(ctx) {
    if (ctx.noData) return;
    qsa(ctx.root, '[data-upc-directory] a[data-upc-model-link]').forEach(function (a) {
      var m = ctx.index.byId[String(a.getAttribute('data-upc-model-link') || '').toLowerCase()];
      if (!m || m.k === 'n') return;
      var n = core.modelRows(ctx.index, m, ctx.products).length;
      var c = qs(a, '.upc-dir__count');
      if (n > 0) {
        if (!c) {
          c = document.createElement('span');
          c.className = 'upc-dir__count';
          a.insertBefore(c, qs(a, 'svg'));
        }
        setText(c, ctx.str(n === 1 ? 'group_one' : 'group_many', { n: n }));
      } else removeNode(c);
    });
  }

  function startFinder(ctx) {
    var route = core.parseRoute(window.location.search);
    refreshDirectory(ctx);
    renderRoute(ctx, route, { initial: true });
    window.addEventListener('popstate', function (e) {
      if (!ctx.root.isConnected) return;
      renderRoute(ctx, core.parseRoute(window.location.search), { pop: true });
      var st = e.state;
      var y = st && st.upc && typeof st.scroll === 'number' ? st.scroll : null;
      if (y != null) raf(function () { try { window.scrollTo(0, y); } catch (err) { /* jsdom */ } });
    });
  }

  function renderRoute(ctx, route, opts) {
    opts = opts || {};
    ctx.token += 1;
    var focus = !opts.initial;
    if (route.kind !== 'default' && ctx.noData) route = { kind: 'default' };
    if (route.kind === 'model') {
      var m = core.resolveModel(ctx.index, route.model);
      if (!m) { renderDefault(ctx, true); ctx.announce(ctx.str('model_unknown')); return; }
      if (route.model !== m.id && (opts.initial || opts.push)) replaceUrl(ctx, { kind: 'model', model: m.id }, route.type);
      renderModelView(ctx, m, route, focus);
    } else if (route.kind === 'pn') {
      renderPn(ctx, route.pn, focus);
    } else {
      renderDefault(ctx, false);
      if (focus) focusEl(focusable(qs(ctx.root, '[data-upc-view="default"] [data-upc-h1]')));
    }
  }

  function renderDefault(ctx, unknown) {
    showView(ctx, 'default');
    ctx.model = null;
    ctx.pn = null;
    setCrumb(ctx, '');
    var notice = qs(ctx.root, '[data-upc-notice]');
    if (notice) {
      setText(slotOf(notice, 'text'), unknown ? ctx.str('model_unknown') : '');
      setHidden(notice, !unknown);
    }
  }

  function renderModelView(ctx, model, route, focus) {
    var host = views(ctx).res;
    if (!host) return;
    var res = renderModelInto(ctx, host, model, {});
    showView(ctx, 'results');
    var notice = qs(ctx.root, '[data-upc-notice]');
    setHidden(notice, true);
    ctx.model = model;
    setCrumb(ctx, model.n);
    if (model.k !== 'n') remember(ctx, model);
    ctx.announce(liveFor(ctx, model, res.n));
    if (focus && res.h1) { focusEl(focusable(res.h1)); revealEl(ctx, res.h1); }
    if (route && route.type) selectGroup(ctx, route.type, true);
  }

  /* ---------- URL state ---------- */
  function currentState() {
    var s = {};
    try {
      var cur = window.history.state;
      if (cur && typeof cur === 'object') Object.keys(cur).forEach(function (k) { s[k] = cur[k]; });
    } catch (e) { /* ignore */ }
    s.upc = 1;
    s.scroll = scrollY();
    return s;
  }
  function saveScroll() { try { window.history.replaceState(currentState(), ''); } catch (e) { /* sandboxed */ } }
  /* the same page with the state keys replaced (a typed name becomes the model id); a ?type= stays */
  function replaceUrl(ctx, route, type) {
    var search = core.routeSearch(window.location.search, route);
    if (type) search += (search ? '&' : '?') + 'type=' + encodeURIComponent(type);
    try { window.history.replaceState(currentState(), '', window.location.pathname + search + window.location.hash); } catch (e) { /* sandboxed */ }
  }
  function mergeState(extra) {
    var s = currentState();
    Object.keys(extra).forEach(function (k) { s[k] = extra[k]; });
    return s;
  }
  function sameRoute(a, b) {
    if (!a || !b || a.kind !== b.kind) return false;
    if (a.kind === 'model') return a.model === b.model;
    if (a.kind === 'pn') return a.pn === b.pn;
    return true;
  }
  /* push a state (finder), render it, move focus to the heading */
  function navigate(ctx, route, opts) {
    opts = opts || {};
    var href = window.location.pathname + core.routeSearch(window.location.search, route);
    var cur = core.parseRoute(window.location.search);
    try {
      saveScroll();
      if (opts.replace || sameRoute(route, cur)) window.history.replaceState({ upc: 1, scroll: 0 }, '', href);
      else window.history.pushState({ upc: 1, scroll: 0 }, '', href);
    } catch (e) { /* sandboxed */ }
    renderRoute(ctx, route, { push: true });
  }

  /* ---------- remembered model ---------- */
  function remember(ctx, model) { if (model && model.id) sstSet(ctx.storageKey, model.id); }
  function recall(ctx) {
    var id = sstGet(ctx.storageKey);
    if (!id) return null;
    var m = ctx.index.byId[String(id).toLowerCase()] || null;
    if (!m) sstDel(ctx.storageKey);
    return m;
  }

  /* ---------- part-number results (D04, D05, no match) ---------- */
  function soldIndex(ctx) {
    if (!ctx.soldPn) ctx.soldPn = core.buildPnIndex(ctx.products.list, function (p) { return p.s; });
    return ctx.soldPn;
  }
  function renderPn(ctx, typed, focus) {
    var token = ctx.token;
    var hit = core.lookupPn(soldIndex(ctx), typed);
    if (hit) { renderPnSold(ctx, hit.entry, focus); return; }
    /* not sold online: the catalog asset is fetched once, only now */
    showPnLoading(ctx);
    loadCatalog(ctx).then(function (cat) {
      if (token !== ctx.token) return;
      var found = cat ? core.lookupPn(cat.pnIdx, typed) : null;
      if (found) {
        /* a number sold online under the catalog's spelling is still a sold part */
        var sold = ctx.products.bySku[core.pnUpper(found.entry.pn)];
        if (sold) renderPnSold(ctx, sold, focus); else renderPnQuote(ctx, found.entry, cat.data, focus);
      } else renderPnNoMatch(ctx, typed, focus);
    }).then(null, function () {
      /* an exception while drawing the answer: no claim either way, the no-match state (or the server view if even that fails) */
      if (token !== ctx.token) return;
      try { renderPnNoMatch(ctx, typed, focus); } catch (e) { restoreServerView(ctx); }
    });
  }
  function pnHost(ctx) {
    var host = views(ctx).pn;
    showView(ctx, 'pn');
    ctx.model = null;
    var notice = qs(ctx.root, '[data-upc-notice]');
    setHidden(notice, true);
    return host;
  }
  function showPnLoading(ctx) {
    var host = pnHost(ctx);
    clearNode(host);
    var sk = tplNode(ctx, 'skeleton');
    if (sk) { removeNode(qs(sk, '.upc-skeleton__status')); host.appendChild(sk); }
    host.setAttribute('aria-busy', 'true');
  }
  function finishPn(ctx, host, node, pnText, focus, liveKey) {
    host.removeAttribute('aria-busy');
    clearNode(host);
    host.appendChild(node);
    host.appendChild(tplNode(ctx, 'foot-pn'));
    ctx.pn = pnText;
    setCrumb(ctx, pnText);
    ctx.announce(ctx.str(liveKey, { pn: pnText }));
    var h1 = qs(host, '[data-upc-h1]');
    if (focus && h1) { focusEl(focusable(h1)); revealEl(ctx, h1); }
  }
  function fitsSection(ctx, el, groups, olderShown) {
    var host = slotOf(el, 'fits');
    groups.forEach(function (g) {
      var gr = tplNode(ctx, 'fits-group');
      fillSlot(gr, 'label', g.label);
      var tags = slotOf(gr, 'tags');
      g.names.forEach(function (name) {
        var tag = tplNode(ctx, 'fits-tag');
        fillSlot(tag, 'name', name);
        tags.appendChild(tag);
      });
      host.appendChild(gr);
    });
    setHidden(slotOf(el, 'older-note'), !olderShown);
  }
  function renderPnSold(ctx, prod, focus) {
    var str = ctx.str;
    var el = tplNode(ctx, 'pn-sold');
    var pi = ctx.index.partBySku[prod.s.toUpperCase()];
    var part = pi == null ? null : ctx.index.parts[pi];
    fillSlot(el, 'eyebrow', str('pn_eyebrow'));
    fillSlot(el, 'h1', prod.s);
    fillSlot(el, 'sub', str('pn_sold_sub'));
    /* headings: the h1 is the part number; the searched row is a section of its own (h2), an equivalent row sits under the h2 "Equivalent part" (h3) */
    slotOf(el, 'result').appendChild(buildRow(ctx, part, prod, { details: true, level: 2 }));
    var fits = pi == null ? { groups: [], hasOlder: false } : core.fitGroups(ctx.index, pi, str('older_models'));
    var fitsEl = qs(el, '[data-upc-fits]');
    if (fits.groups.length) {
      fitsSection(ctx, el, fits.groups, fits.hasOlder);
      qsa(fitsEl, '[data-upc-contact]').forEach(function (a) { setContact(ctx, a, { model: '', handle: prod.h, sku: prod.s }); });
    } else removeNode(fitsEl);
    var equiv = qs(el, '[data-upc-equiv]');
    var pairs = [];
    core.equivalents(ctx.index, prod.s).forEach(function (sku) {
      var other = ctx.products.bySku[sku.toUpperCase()];
      if (other && pairs.indexOf(other) === -1) pairs.push(other);
    });
    if (pairs.length) {
      var eh = slotOf(el, 'equiv');
      pairs.forEach(function (o) {
        var opi = ctx.index.partBySku[o.s.toUpperCase()];
        eh.appendChild(buildRow(ctx, opi == null ? null : ctx.index.parts[opi], o, { details: true, ref: prod.s, level: 3 }));
      });
      setHidden(equiv, false);
    } else removeNode(equiv);
    finishPn(ctx, pnHost(ctx), el, prod.s, focus, 'live_pn');
  }
  function renderPnQuote(ctx, entry, catalog, focus) {
    var str = ctx.str;
    var el = tplNode(ctx, 'pn-quote');
    fillSlot(el, 'eyebrow', str('pn_eyebrow'));
    fillSlot(el, 'h1', entry.pn);
    fillSlot(el, 'sub', str('pn_quote_sub'));
    var row = tplNode(ctx, 'pn-quote-row');
    fillSlot(row, 'brand', entry.brand === 'ABI' ? str('brand_abi') : str('brand_oem'));
    fillSlot(row, 'title', entry.desc);
    fillSlot(row, 'pn', entry.pn);
    fillSlot(row, 'status', str('pn_status'));
    setQuote(ctx, slotOf(row, 'cta'), entry.pn);
    slotOf(el, 'result').appendChild(row);
    var fitsEl = qs(el, '[data-upc-fits]');
    var groups = core.catalogFits(catalog, entry);
    if (groups.length) {
      var gh = slotOf(el, 'fits');
      groups.forEach(function (g) {
        var gr = tplNode(ctx, 'fits-group');
        fillSlot(gr, 'label', g.label);
        var tags = slotOf(gr, 'tags');
        g.names.forEach(function (name) { var tag = tplNode(ctx, 'fits-tag'); fillSlot(tag, 'name', name); tags.appendChild(tag); });
        gh.appendChild(gr);
      });
    } else removeNode(fitsEl);
    setQuote(ctx, slotOf(el, 'quote-page-link'), '');
    finishPn(ctx, pnHost(ctx), el, entry.pn, focus, 'live_pn');
  }
  function renderPnNoMatch(ctx, typed, focus) {
    var str = ctx.str;
    var el = tplNode(ctx, 'pn-nomatch');
    var t = core.trim(typed);
    fillSlot(el, 'eyebrow', str('pn_eyebrow'));
    fillSlot(el, 'h1', str('pn_nomatch_h1', { pn: t }));
    /* a typed number that matches nothing cannot be carried to the contact form (the page reads no part-number field): the link carries the topic only */
    qsa(el, '[data-upc-contact]').forEach(function (a) { setContact(ctx, a, { model: '', handle: '', sku: '' }); });
    finishPn(ctx, pnHost(ctx), el, t, focus, 'live_nomatch');
  }

  /* The catalog asset: fetched once (a failure is not cached, the next miss tries again), 8 s timeout. */
  function loadCatalog(ctx) {
    var c = ctx.catalog;
    if (c.status === 'ok') return Promise.resolve(c);
    if (c.promise) return c.promise;
    c.status = 'loading';
    c.promise = new Promise(function (resolve) {
      if (!ctx.catalogUrl || typeof window.fetch !== 'function') { resolve(null); return; }
      var done = false;
      var ctrl = null;
      try { ctrl = typeof window.AbortController === 'function' ? new window.AbortController() : null; } catch (e) { ctrl = null; }
      var timer = setTimeout(function () {
        if (done) return;
        done = true;
        try { if (ctrl) ctrl.abort(); } catch (e) { /* ignore */ }
        resolve(null);
      }, CATALOG_TIMEOUT);
      var opts = { credentials: 'same-origin', headers: { Accept: 'application/json' } };
      if (ctrl) opts.signal = ctrl.signal;
      window.fetch(ctx.catalogUrl, opts).then(function (r) {
        if (!r.ok) throw new Error('catalog');
        return r.json();
      }).then(function (data) {
        if (done) return;
        done = true;
        clearTimeout(timer);
        if (!data || !Array.isArray(data.parts)) { resolve(null); return; }
        c.data = data;
        c.pnIdx = core.buildPnIndex(data.parts, function (p) { return p.pn; });
        c.status = 'ok';
        resolve(c);
      }).then(null, function () {
        if (done) return;
        done = true;
        clearTimeout(timer);
        resolve(null);
      });
    }).then(function (res) {
      c.promise = null;
      if (!res) c.status = 'idle';
      return res;
    });
    return c.promise;
  }

  /* ---------- model page (mode "model") ---------- */
  function startModelPage(ctx) {
    var root = ctx.root;
    var model = core.resolveModel(ctx.index, root.getAttribute('data-upc-model') || ctx.cfg.model_id);
    var host = qs(root, '[data-upc-view="results"]');
    ctx.model = model;
    if (!model || !host || ctx.noData) return; /* the server rows stay: no store data, nothing to claim */
    var res = renderModelInto(ctx, host, model, {});
    if (model.k !== 'n') remember(ctx, model);
    ctx.announce(liveFor(ctx, model, res.n));
    ctx.panel = qs(root, '[data-upc-change-panel]');
    var type = core.trim(core.parseQuery(window.location.search).type);
    if (type) selectGroup(ctx, type, true);
  }
  function toggleChangePanel(ctx, btn, force) {
    var panel = ctx.panel || qs(ctx.root, '[data-upc-change-panel]');
    if (!panel) return;
    var open = typeof force === 'boolean' ? force : panel.hasAttribute('hidden');
    setHidden(panel, !open);
    qsa(ctx.root, '[data-upc-change-model]').forEach(function (b) {
      b.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (panel.id) b.setAttribute('aria-controls', panel.id);
    });
    if (open) focusEl(qs(panel, '[data-upc-model-input]'));
    else focusEl(btn || qs(ctx.root, '[data-upc-change-model]'));
  }

  /* ---------- browse (mode "browse", D03) ---------- */
  function startBrowse(ctx) {
    var root = ctx.root;
    var cards = qsa(root, '[data-upc-card]');
    ctx.b = {
      cards: cards, grid: qs(root, '[data-upc-grid]'), type: '', model: null, parts: {},
      idle: qs(root, '[data-upc-yours-idle]'), applied: qs(root, '[data-upc-yours-applied]'), chips: qs(root, '[data-upc-browse-chips]'),
      unlisted: qs(root, '[data-upc-unlisted]'), unlistedList: qs(root, '[data-upc-unlisted-list]'), yoursLink: qs(root, '[data-upc-yours-link]'),
      unknown: qs(root, '[data-upc-unknown]'), unknownList: qs(root, '[data-upc-unknown-list]')
    };
    var route = core.parseRoute(window.location.search);
    var wanted = null;
    var st = window.history.state;
    if (st && st.upc && typeof st.model === 'string') wanted = st.model ? core.resolveModel(ctx.index, st.model) : null;
    else if (route.kind === 'model') {
      wanted = core.resolveModel(ctx.index, route.model);
      if (!wanted) ctx.announce(ctx.str('model_unknown'));
    } else wanted = recall(ctx);
    try { window.history.replaceState(mergeState({ model: wanted && !ctx.noData ? wanted.id : '' }), ''); } catch (e) { /* sandboxed */ }
    if (wanted && !ctx.noData) applyBrowse(ctx, wanted, { initial: true });
    window.addEventListener('popstate', function (e) {
      if (!ctx.root.isConnected) return;
      var m = null;
      var s = e.state;
      if (s && s.upc && typeof s.model === 'string') m = s.model ? core.resolveModel(ctx.index, s.model) : null;
      else { var r = core.parseRoute(window.location.search); m = r.kind === 'model' ? core.resolveModel(ctx.index, r.model) : null; }
      if (m) applyBrowse(ctx, m, { pop: true }); else clearBrowse(ctx, { pop: true });
    });
  }
  function cardPart(ctx, card) {
    var pi = ctx.index.partBySku[(card.getAttribute('data-sku') || '').toUpperCase()];
    return pi == null ? -1 : pi;
  }
  function cardKey(ctx, card) {
    var pi = cardPart(ctx, card);
    if (pi < 0) return '';
    var p = ctx.index.parts[pi];
    return p.t ? p.t : 'g:' + p.g;
  }
  function keyLabel(ctx, key) {
    if (key.indexOf('g:') === 0) { var g = ctx.index.groupById[key.slice(2)]; return g ? groupLabel(ctx, g) : ''; }
    return ctx.index.types[key] || key;
  }
  function removeBrowseNotes(ctx) { qsa(ctx.root, '[data-upc-browse-note]').forEach(removeNode); }
  function browseNote(ctx, name) {
    var n = tplInner(ctx, name);
    if (!n) return null;
    n.setAttribute('data-upc-browse-note', '');
    var y = qs(ctx.root, '[data-upc-yours]');
    if (y && y.parentNode) y.parentNode.insertBefore(n, y.nextSibling);
    return n;
  }
  function resetCards(ctx) {
    var b = ctx.b;
    b.cards.forEach(function (c) {
      setHidden(c, false);
      var fit = slotOf(c, 'fit');
      setHidden(fit, true);
      setText(slotOf(c, 'fit-text'), '');
      if (b.grid) b.grid.appendChild(c);
      qsa(c, '[data-upc-contact]').forEach(function (a) { setContact(ctx, a, { model: '' }); });
    });
  }
  function applyBrowse(ctx, model, opts) {
    opts = opts || {};
    var b = ctx.b;
    var str = ctx.str;
    /* an empty collection: nothing to filter and nothing to count (the server prints the empty line and hides the bar) */
    if (!b.cards.length) return;
    removeBrowseNotes(ctx);
    if (model.k === 'n') {
      /* no parts list: nothing to filter by and nothing to claim */
      if (b.model) resetBrowse(ctx);
      b.model = null;
      setHidden(b.idle, false);
      setHidden(b.applied, true);
      var nl = browseNote(ctx, 'nolist');
      if (nl) fillNolist(ctx, nl, model);
      ctx.announce(liveFor(ctx, model, 0));
      return;
    }
    if (b.model) resetBrowse(ctx);
    b.model = model;
    b.type = '';
    var fitSet = {};
    model.p.forEach(function (pi) { fitSet[pi] = true; });
    var fitting = [];
    var other = [];
    var unknown = [];
    /* three kinds: on the model's list; a mapped part that is not on it; a product the mapping has no model list for
       (no fit data: it is shown apart, without "Fits" and without "isn't listed for") */
    b.cards.forEach(function (c) {
      var pi = cardPart(ctx, c);
      if (pi < 0) { if (b.unknownList) unknown.push(c); return; }
      if (fitSet[pi]) fitting.push(c); else other.push(c);
    });
    unknown.forEach(function (c) {
      b.unknownList.appendChild(c);
      qsa(c, '[data-upc-contact]').forEach(function (a) { setContact(ctx, a, { model: model.n }); });
    });
    if (b.unknown) {
      if (unknown.length) fillSlot(b.unknown, 'title', str(unknown.length === 1 ? 'unknown_one' : 'unknown_title', { n: unknown.length }));
      setHidden(b.unknown, !unknown.length);
      setHidden(b.unknownList, !unknown.length);
    }
    /* "Fits {model}" on the cards that fit; the rest move into the collapsed block */
    fitting.forEach(function (c) {
      var fit = slotOf(c, 'fit');
      setText(slotOf(c, 'fit-text'), str('fits', { model: model.n }));
      setHidden(fit, false);
      qsa(c, '[data-upc-contact]').forEach(function (a) { setContact(ctx, a, { model: model.n }); });
    });
    other.forEach(function (c) { if (b.unlistedList) b.unlistedList.appendChild(c); });
    if (b.unlisted) {
      if (other.length) {
        fillSlot(b.unlisted, 'title', str(other.length === 1 ? 'unlisted_one' : 'unlisted_title', { n: other.length, model: model.n }));
        setHidden(b.unlisted, false);
        var tg = qs(b.unlisted, '[data-upc-unlisted-toggle]');
        setText(tg, str('unlisted_show'));
        tg.setAttribute('aria-expanded', 'false');
        setHidden(b.unlistedList, true);
      } else setHidden(b.unlisted, true);
    }
    /* the bar: "Showing parts that fit your {model}" */
    setHidden(b.idle, true);
    clearNode(b.applied);
    var y = tplNode(ctx, 'yours-applied');
    var fam = ctx.index.families[model.f] || {};
    var img = slotOf(y, 'img');
    if (img) { if (fam.i) img.setAttribute('src', fam.i); else { removeNode(img); setHidden(qs(y, '.upc-yours__img'), true); } }
    fillSlot(y, 'title', str('yours_title', { model: model.n }));
    fillSlot(y, 'sub', str('yours_sub', { n: fitting.length, total: b.cards.length }));
    fillSlot(y, 'show-all', str('yours_show_all', { total: b.cards.length }));
    b.applied.appendChild(y);
    setHidden(b.applied, false);
    if (model.k === 'k') {
      var kb = browseNote(ctx, 'kb-note');
      if (kb) fillSlot(kb, 'text', str('kb_note'));
    }
    /* type chips: only what fits, counts of what is visible, in the map's order; none when there is nothing to choose between */
    var counts = {};
    var first = {};
    var order = [];
    fitting.forEach(function (c) {
      var k = cardKey(ctx, c);
      if (!(k in counts)) { counts[k] = 0; first[k] = cardPart(ctx, c); order.push(k); }
      counts[k] += 1;
      first[k] = Math.min(first[k], cardPart(ctx, c));
    });
    order.sort(function (a, z) { return first[a] - first[z]; });
    clearNode(b.chips);
    if (order.length > 1) {
      var all = tplNode(ctx, 'browse-chip');
      fillSlot(all, 'label', str('browse_chip_all') || str('chip_all'));
      fillSlot(all, 'count', String(fitting.length));
      all.setAttribute('aria-pressed', 'true');
      all.setAttribute('data-type', '');
      b.chips.appendChild(all);
      order.forEach(function (k) {
        var c = tplNode(ctx, 'browse-chip');
        fillSlot(c, 'label', keyLabel(ctx, k));
        fillSlot(c, 'count', String(counts[k]));
        c.setAttribute('data-type', k);
        b.chips.appendChild(c);
      });
      setHidden(b.chips, false);
    } else setHidden(b.chips, true);
    if (b.yoursLink) {
      var a = slotOf(b.yoursLink, 'model-parts-link');
      setText(a, str('yours_link', { model: model.n }));
      a.setAttribute('href', ctx.finderUrl + core.routeSearch('', { kind: 'model', model: model.id }));
      setHidden(b.yoursLink, false);
    }
    qsa(ctx.root, '.upc-quote [data-upc-contact]').forEach(function (a2) { setContact(ctx, a2, { model: model.n }); });
    remember(ctx, model);
    if (opts.push) writeBrowseUrl(ctx, { kind: 'model', model: model.id }, model.id, false);
    ctx.announce(fitting.length === 1 ? str('live_one', { model: model.n }) : str('live_many', { n: fitting.length, model: model.n }));
    if (opts.focus) focusEl(qs(b.applied, '[data-upc-change-model]'));
  }
  function resetBrowse(ctx) {
    var b = ctx.b;
    resetCards(ctx);
    setHidden(b.unlisted, true);
    setHidden(b.unlistedList, true);
    setHidden(b.unknown, true);
    setHidden(b.unknownList, true);
    setHidden(b.chips, true);
    clearNode(b.chips);
    setHidden(b.yoursLink, true);
    qsa(ctx.root, '.upc-quote [data-upc-contact]').forEach(function (a) { setContact(ctx, a, { model: '' }); });
    b.model = null;
    b.type = '';
  }
  /* the URL follows the browse state: ?model=<id> when a model is applied, none otherwise (history state carries the id) */
  function writeBrowseUrl(ctx, route, id, replace) {
    var href = window.location.pathname + core.routeSearch(window.location.search, route);
    try {
      if (replace) window.history.replaceState(mergeState({ model: id }), '', href);
      else { saveScroll(); window.history.pushState({ upc: 1, scroll: 0, model: id }, '', href); }
    } catch (e) { /* sandboxed */ }
  }
  /* "Show all" (forget: the remembered model is dropped) and "Change model" (the filter is lifted, the choice stays remembered) */
  function clearBrowse(ctx, opts) {
    opts = opts || {};
    var b = ctx.b;
    removeBrowseNotes(ctx);
    if (b.model) resetBrowse(ctx);
    setHidden(b.idle, false);
    clearNode(b.applied);
    setHidden(b.applied, true);
    if (opts.forget) sstDel(ctx.storageKey);
    if (opts.push) writeBrowseUrl(ctx, { kind: 'default' }, '', false);
    else if (opts.replace) writeBrowseUrl(ctx, { kind: 'default' }, '', true);
    if (!opts.quiet) ctx.announce(ctx.str('group_many', { n: b.cards.length }));
    if (opts.focus) focusEl(qs(b.idle, '[data-upc-model-input]'));
  }
  function filterBrowseType(ctx, key) {
    var b = ctx.b;
    if (!b.model) return;
    b.type = key;
    var shown = 0;
    b.cards.forEach(function (c) {
      if (c.parentNode !== b.grid) return;
      var on = !key || cardKey(ctx, c) === key;
      setHidden(c, !on);
      if (on) shown += 1;
    });
    qsa(b.chips, '[data-upc-chip]').forEach(function (c) { c.setAttribute('aria-pressed', (c.getAttribute('data-type') || '') === key ? 'true' : 'false'); });
    ctx.announce(shown === 1 ? ctx.str('live_one', { model: b.model.n }) : ctx.str('live_many', { n: shown, model: b.model.n }));
  }

  /* ---------- model chosen (combobox, links) ---------- */
  function modelHref(ctx, m) {
    return m.h ? '/collections/' + m.h : ctx.finderUrl + core.routeSearch('', { kind: 'model', model: m.id });
  }
  function chooseModel(ctx, m) {
    if (ctx.mode === 'finder') {
      if (ctx.noData) { if (m.h) go(ctx, modelHref(ctx, m)); return; }
      navigate(ctx, { kind: 'model', model: m.id });
    } else if (ctx.mode === 'browse') {
      if (ctx.noData) return;
      applyBrowse(ctx, m, { push: m.k !== 'n', focus: true });
    } else if (ctx.mode === 'model') {
      if (ctx.model && ctx.model.id === m.id) { toggleChangePanel(ctx, null, false); return; }
      go(ctx, modelHref(ctx, m));
    }
  }
  function unknownModel(ctx, typed, box) {
    if (ctx.mode === 'finder' && !ctx.noData) { navigate(ctx, { kind: 'model', model: typed }); return; }
    ctx.announce(ctx.str('model_unknown'));
    if (box) comboMessage(ctx, box, ctx.str('model_unknown'));
  }
  function comboMessage(ctx, box, msg) {
    var wrap = box.wrap;
    var p = qs(wrap.parentNode, '[data-upc-combo-msg]');
    if (!msg) { removeNode(p); return; }
    if (!p) {
      p = document.createElement('p');
      p.className = 'upc-hint';
      p.setAttribute('data-upc-combo-msg', '');
      p.setAttribute('role', 'status');
      wrap.parentNode.insertBefore(p, wrap.nextSibling);
    }
    setText(p, msg);
  }

  /* ---------- the model combobox (ARIA 1.2 editable combobox with a listbox popup) ---------- */
  function initCombobox(ctx, wrap) {
    var str = ctx.str;
    var input = qs(wrap, 'input[data-upc-model-input]');
    var toggle = qs(wrap, 'button[data-upc-model-toggle]');
    var list = qs(wrap, '[data-upc-model-list]');
    if (!input || !list) return;
    var n = ++boxSeq;
    if (!list.id) list.id = 'upc-lb-' + n;
    var listId = list.id;
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-controls', listId);
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-expanded', 'false');
    list.setAttribute('role', 'listbox');
    var st = { open: false, all: false, results: [], active: -1, wrap: wrap, input: input, list: list };
    ctx.boxes.push(st);

    function shown() { return !list.hasAttribute('hidden'); }
    function optId(i) { return listId + '-o' + i; }
    function onlineCount(m) {
      if (!ctx.counts) ctx.counts = {};
      if (ctx.counts[m.id] == null) ctx.counts[m.id] = core.modelRows(ctx.index, m, ctx.products).length;
      return ctx.counts[m.id];
    }
    function updateActive() {
      var opts = qsa(list, '[role="option"]');
      opts.forEach(function (li, i) {
        li.setAttribute('aria-selected', i === st.active ? 'true' : 'false');
        if (i === st.active) li.setAttribute('data-active', 'true'); else li.removeAttribute('data-active');
      });
      var cur = st.active >= 0 ? opts[st.active] : null;
      if (cur && shown()) {
        input.setAttribute('aria-activedescendant', cur.id);
        if (cur.scrollIntoView) { try { cur.scrollIntoView({ block: 'nearest' }); } catch (e) { /* old engines */ } }
      } else input.removeAttribute('aria-activedescendant');
    }
    function render() {
      var q = input.value;
      st.results = st.open ? core.searchModels(ctx.index, st.all ? '' : q) : [];
      if (st.active >= st.results.length) st.active = -1;
      clearNode(list);
      var last = -1;
      var into = null;
      st.results.forEach(function (r, i) {
        if (r.model.f !== last) {
          var g = tplNode(ctx, 'opt-group');
          var fam = ctx.index.families[r.model.f] || {};
          var labelEl = slotOf(g, 'label');
          setText(labelEl, fam.l || '');
          labelEl.id = listId + '-g' + r.model.f;
          var ul = slotOf(g, 'options');
          ul.setAttribute('aria-labelledby', labelEl.id);
          list.appendChild(g);
          into = ul;
          last = r.model.f;
        }
        var o = tplNode(ctx, 'opt');
        o.id = optId(i);
        o.setAttribute('data-index', String(i));
        o.setAttribute('data-model', r.model.id);
        fillSlot(o, 'name', r.model.n);
        var c = r.model.k === 'n' ? 0 : onlineCount(r.model);
        var cs = slotOf(o, 'count');
        if (c > 0) setText(cs, str(c === 1 ? 'group_one' : 'group_many', { n: c })); else removeNode(cs);
        into.appendChild(o);
      });
      var show = st.open && st.results.length > 0;
      setHidden(list, !show);
      input.setAttribute('aria-expanded', show ? 'true' : 'false');
      if (toggle) toggle.setAttribute('aria-expanded', show ? 'true' : 'false');
      updateActive();
    }
    st.close = function () { st.open = false; st.all = false; st.active = -1; render(); };
    function choose(m) {
      if (!m) return;
      st.close();
      input.value = '';
      comboMessage(ctx, st, '');
      chooseModel(ctx, m);
    }
    function openList(all, activeLast) {
      st.open = true;
      st.all = !!all;
      render();
      if (activeLast != null) { st.active = st.results.length ? (activeLast ? st.results.length - 1 : 0) : -1; updateActive(); }
    }
    /* Enter, and a form submit: an exact name or alias, a single match, the active option, else the unknown-model state */
    function submitText() {
      if (shown() && st.active >= 0 && st.results[st.active]) { choose(st.results[st.active].model); return; }
      var q = core.trim(input.value);
      if (!core.norm(q)) { focusEl(input); return; }
      var exact = core.resolveModel(ctx.index, q);
      if (exact) { choose(exact); return; }
      var res = core.searchModels(ctx.index, q);
      if (res.length === 1) { choose(res[0].model); return; }
      if (!res.length) { st.close(); input.value = ''; unknownModel(ctx, q, st); return; }
      openList(false, null);
    }
    st.submit = submitText;
    input.addEventListener('input', function () {
      st.all = false;
      st.open = core.norm(input.value).length > 0;
      st.active = -1;
      comboMessage(ctx, st, '');
      render();
    });
    input.addEventListener('keydown', function (e) {
      var k = e.key;
      if (k === 'ArrowDown' || k === 'Down') {
        e.preventDefault();
        if (e.altKey) { openList(true, null); return; }
        if (!shown()) { openList(!core.norm(input.value), false); return; }
        st.active = st.active < 0 ? 0 : Math.min(st.active + 1, st.results.length - 1);
        updateActive();
      } else if (k === 'ArrowUp' || k === 'Up') {
        e.preventDefault();
        if (e.altKey) { st.close(); return; }
        if (!shown()) { openList(!core.norm(input.value), true); return; }
        st.active = st.active <= 0 ? 0 : st.active - 1;
        updateActive();
      } else if ((k === 'Home' || k === 'End') && shown() && st.active >= 0) {
        e.preventDefault();
        st.active = k === 'Home' ? 0 : st.results.length - 1;
        updateActive();
      } else if (k === 'Enter') {
        e.preventDefault();
        submitText();
      } else if (k === 'Escape' || k === 'Esc') {
        if (shown()) { e.preventDefault(); e.stopPropagation(); st.close(); return; }
        var panel = closest(wrap, '[data-upc-change-panel]');
        if (panel && !panel.hasAttribute('hidden')) { e.preventDefault(); toggleChangePanel(ctx, null, false); }
      } else if (k === 'Tab') {
        if (st.open) st.close();
      }
    });
    input.addEventListener('blur', function () {
      setTimeout(function () {
        if (document.activeElement === input || (toggle && document.activeElement === toggle)) return;
        if (st.open) st.close();
      }, 0);
    });
    list.addEventListener('mousedown', function (e) { e.preventDefault(); });
    list.addEventListener('click', function (e) {
      var li = closest(e.target, '[role="option"]');
      if (!li) return;
      var r = st.results[parseInt(li.getAttribute('data-index'), 10)];
      if (r) choose(r.model);
    });
    list.addEventListener('mousemove', function (e) {
      var li = closest(e.target, '[role="option"]');
      if (!li) return;
      var i = parseInt(li.getAttribute('data-index'), 10);
      if (!isNaN(i) && i !== st.active) { st.active = i; updateActive(); }
    });
    if (toggle) {
      toggle.setAttribute('type', 'button');
      toggle.setAttribute('aria-expanded', 'false');
      toggle.addEventListener('mousedown', function (e) { if (shown()) e.preventDefault(); });
      toggle.addEventListener('click', function (e) {
        e.preventDefault();
        if (shown()) { st.close(); focusEl(input); return; }
        openList(true, null);
        focusEl(input);
      });
    }
    render();
  }

  /* ---------- cart ---------- */
  function cartUrl() {
    var r = '/';
    try { if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) r = String(window.Shopify.routes.root); } catch (e) { r = '/'; }
    return (r.charAt(r.length - 1) === '/' ? r : r + '/') + 'cart/add.js';
  }
  /* One POST /cart/add.js with items[]; 15 s timeout; requests never overlap (page-wide chain).
     Resolves { key, data }; rejects { kind: 'net' | 'timeout' | '422', message }. */
  function cartAdd(items) {
    var run = function () {
      return new Promise(function (resolve, reject) {
        if (typeof window.fetch !== 'function') { reject({ kind: 'net', message: '' }); return; }
        var done = false;
        var ctrl = null;
        try { ctrl = typeof window.AbortController === 'function' ? new window.AbortController() : null; } catch (e) { ctrl = null; }
        var timer = setTimeout(function () {
          if (done) return;
          done = true;
          try { if (ctrl) ctrl.abort(); } catch (e) { /* ignore */ }
          reject({ kind: 'timeout', message: '' });
        }, CART_TIMEOUT);
        var opts = {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items: items })
        };
        if (ctrl) opts.signal = ctrl.signal;
        window.fetch(cartUrl(), opts).then(function (r) {
          return r.json().then(null, function () { return {}; }).then(function (data) { return { ok: r.ok, status: r.status, data: data || {} }; });
        }).then(function (res) {
          if (done) return;
          done = true;
          clearTimeout(timer);
          if (!res.ok) {
            var msg = core.trim(res.data.description || res.data.message || '').replace(/[.]+$/, '');
            reject({ kind: res.status === 422 && msg ? '422' : 'net', message: msg });
            return;
          }
          var first = (Array.isArray(res.data.items) && res.data.items[0]) || res.data;
          resolve({ key: first && first.key ? String(first.key) : null, data: res.data });
        }, function () {
          if (done) return;
          done = true;
          clearTimeout(timer);
          reject({ kind: 'net', message: '' });
        });
      });
    };
    var prev = cart.chain || Promise.resolve();
    var p = prev.then(run, run);
    cart.chain = p.then(function () { return null; }, function () { return null; });
    return p;
  }
  function afterAdd(ctx, key) {
    if (typeof window.openFlyoutCart === 'function') {
      try { window.openFlyoutCart({ addedKey: key }); return 'drawer'; } catch (e) { /* fall back */ }
    }
    var trig = document.querySelector('.flyout-trigger');
    if (trig) { trig.click(); return 'trigger'; }
    go(ctx, '/cart');
    return 'cart';
  }
  /* While busy the button keeps exactly the width it had (spinner and "Adding…" are a few pixels wider than "Add to cart" on a 320 px row,
     which would push the button onto its own line) */
  function lockWidth(btn) {
    if (!btn || !btn.getBoundingClientRect) return;
    var w = btn.getBoundingClientRect().width;
    if (w > 0) { btn.style.minWidth = w + 'px'; btn.style.maxWidth = w + 'px'; btn.style.whiteSpace = 'nowrap'; }
  }
  function unlockWidth(btn) { btn.style.minWidth = ''; btn.style.maxWidth = ''; btn.style.whiteSpace = ''; }
  function setBusyUi(ctx, btn, on) {
    cart.busy = on;
    qsa(ctx.root, '[data-upc-add]').forEach(function (b) { if (on) b.setAttribute('aria-disabled', 'true'); else { b.removeAttribute('aria-disabled'); b.removeAttribute('data-busy'); } });
    if (!btn) return;
    var label = slotOf(btn, 'add-label');
    var spin = qs(btn, '[data-upc-spinner]');
    if (on) {
      lockWidth(btn);
      btn.setAttribute('data-busy', '');
      if (spin) setHidden(spin, false);
      if (label) { btn.__label = label.textContent; setText(label, ctx.str('adding')); }
      else { btn.__label = btn.textContent; setText(btn, ctx.str('adding')); }
    } else {
      unlockWidth(btn);
      if (spin) setHidden(spin, true);
      if (label) setText(label, btn.__label != null ? btn.__label : ctx.str('add'));
      else if (btn.__label != null) setText(btn, btn.__label);
    }
  }
  function errorSlot(host) {
    var e = slotOf(host, 'error');
    if (!e) {
      e = document.createElement('p');
      e.className = 'upc-row__error';
      e.setAttribute('role', 'alert');
      e.setAttribute('data-upc-slot', 'error');
      e.setAttribute('hidden', '');
      host.appendChild(e);
    }
    return e;
  }
  function addToCart(ctx, host, btn, item, onOk) {
    if (cart.busy || (btn && btn.getAttribute('aria-disabled') === 'true')) return;
    var err = errorSlot(host);
    setText(err, '');
    setHidden(err, true);
    setBusyUi(ctx, btn, true);
    cartAdd([item]).then(function (res) {
      setBusyUi(ctx, btn, false);
      if (onOk) onOk();
      afterAdd(ctx, res.key);
    }, function (e) {
      setBusyUi(ctx, btn, false);
      var msg = e && e.kind === '422' ? ctx.str('cart_error_422', { message: e.message }) : ctx.str('cart_error');
      setText(err, msg);
      setHidden(err, !msg);
      ctx.announce(msg);
    });
  }
  function itemId(v) { var n = Number(v); return isFinite(n) && String(n) === String(v).replace(/^\s+|\s+$/g, '') ? n : v; }

  /* ---------- delegated events ---------- */
  function setQty(row, v) {
    var input = qs(row, '[data-upc-qty-input]');
    if (!input) return;
    var q = core.clampQty(v);
    input.value = String(q);
    var dec = qs(row, '[data-upc-dec]');
    if (dec) dec.disabled = q <= 1;
  }
  function plainClick(e) { return !(e.defaultPrevented || e.button > 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey); }

  function goDefault(ctx, focusSel) {
    navigate(ctx, { kind: 'default' });
    var t = focusSel ? qs(ctx.root, focusSel) : null;
    if (t) { focusEl(t); revealEl(ctx, t); }
  }

  function initEvents(ctx) {
    var root = ctx.root;
    var finderMode = ctx.mode === 'finder';

    root.addEventListener('click', function (e) {
      var t = e.target;
      var el;

      /* quantity stepper */
      el = closest(t, '[data-upc-inc], [data-upc-dec]');
      if (el) {
        var row = closest(el, '[data-upc-row]');
        var cur = qs(row, '[data-upc-qty-input]');
        if (cur) { e.preventDefault(); setQty(row, core.clampQty(cur.value) + (el.hasAttribute('data-upc-inc') ? 1 : -1)); }
        return;
      }
      /* add to cart from a templated row (the server forms are handled on submit) */
      el = closest(t, '[data-upc-add]');
      if (el && !closest(el, 'form[data-upc-add-form]')) {
        e.preventDefault();
        var r2 = closest(el, '[data-upc-row]');
        var prod = r2 ? ctx.products.bySku[(r2.getAttribute('data-sku') || '').toUpperCase()] : null;
        if (!prod || prod.x || prod.a !== true) return;
        var qin = qs(r2, '[data-upc-qty-input]');
        addToCart(ctx, r2, el, { id: itemId(prod.v), quantity: core.clampQty(qin ? qin.value : 1) }, function () { setQty(r2, 1); });
        return;
      }
      if (!plainClick(e)) return;

      /* "Show n more" */
      el = closest(t, '[data-upc-more-row] [data-upc-more]');
      if (el) {
        e.preventDefault();
        expandMore(ctx, closest(el, '[data-upc-more-row]'));
        return;
      }
      /* category chips */
      el = closest(t, '.upc-bar [data-upc-chip]');
      if (el) {
        e.preventDefault();
        selectGroup(ctx, el.getAttribute('data-group') || '', false);
        return;
      }
      /* browse type chips */
      el = closest(t, '[data-upc-browse-chips] [data-upc-chip]');
      if (el) { e.preventDefault(); filterBrowseType(ctx, el.getAttribute('data-type') || ''); return; }
      /* browse: collapsed block */
      el = closest(t, '[data-upc-unlisted-toggle]');
      if (el) {
        e.preventDefault();
        var list = ctx.b && ctx.b.unlistedList;
        if (!list) return;
        var open = list.hasAttribute('hidden');
        setHidden(list, !open);
        el.setAttribute('aria-expanded', open ? 'true' : 'false');
        setText(el, ctx.str(open ? 'unlisted_hide' : 'unlisted_show'));
        return;
      }
      /* browse: show all (forgets the remembered model) */
      el = closest(t, '[data-upc-show-all]');
      if (el) { e.preventDefault(); clearBrowse(ctx, { push: true, forget: true, focus: true }); return; }
      /* "Change model" */
      el = closest(t, '[data-upc-change-model]');
      if (el) {
        e.preventDefault();
        if (ctx.mode === 'finder') goDefault(ctx, '[data-upc-model-input]');
        else if (ctx.mode === 'model') toggleChangePanel(ctx, el);
        else if (ctx.mode === 'browse') clearBrowse(ctx, { focus: true, quiet: true, replace: true });
        return;
      }
      /* finder: links that stay on the page */
      if (finderMode) {
        /* "See all models it fits": the part-number result, in this page */
        el = closest(t, 'a[data-upc-fits-link]');
        if (el) {
          if (!ctx.noData) { e.preventDefault(); navigate(ctx, { kind: 'pn', pn: el.getAttribute('data-upc-fits-link') }); }
          return;
        }
        el = closest(t, 'a[data-upc-model-link]');
        if (el) {
          var href = el.getAttribute('href') || '';
          var base = ctx.finderUrl + '?model=';
          if (href.indexOf(base) === 0) {
            var m = core.resolveModel(ctx.index, core.parseQuery(href.slice(ctx.finderUrl.length)).model);
            if (m && !ctx.noData) { e.preventDefault(); navigate(ctx, { kind: 'model', model: m.id }); }
          }
          return;
        }
        el = closest(t, 'a[data-upc-to-pn]');
        if (el) { e.preventDefault(); goDefault(ctx, '[data-upc-pn-input]'); return; }
        el = closest(t, 'a[data-upc-to-model]');
        if (el) { e.preventDefault(); goDefault(ctx, '[data-upc-model-input]'); return; }
        el = closest(t, 'a[data-upc-crumb-finder]');
        if (el) {
          e.preventDefault();
          if (ctx.view !== 'default') goDefault(ctx, '[data-upc-view="default"] [data-upc-h1]');
          return;
        }
        el = closest(t, 'a[data-upc-all-models]');
        if (el) {
          e.preventDefault();
          scrollToEl(ctx, qs(root, '[data-upc-directory]'), 0, false);
          return;
        }
      }
    });

    root.addEventListener('submit', function (e) {
      var f = e.target;
      if (!f || !f.hasAttribute) return;
      if (f.hasAttribute('data-upc-add-form')) {
        e.preventDefault();
        var idEl = f.elements ? f.elements.id : null;
        var qEl = f.elements ? f.elements.quantity : null;
        var btn = qs(f, '[data-upc-add]');
        if (!idEl) return;
        addToCart(ctx, closest(f, '[data-upc-card], [data-upc-row]') || f, btn, { id: itemId(idEl.value), quantity: core.clampQty(qEl ? qEl.value : 1) }, function () {
          if (qEl && qEl.type === 'number') qEl.value = '1';
        });
      } else if (f.hasAttribute('data-upc-model-form')) {
        e.preventDefault();
        var wrap = closest(f, '[data-upc-combo]');
        var box = null;
        ctx.boxes.forEach(function (b) { if (b.wrap === wrap) box = b; });
        if (box) box.submit();
      } else if (f.hasAttribute('data-upc-pn-form')) {
        e.preventDefault();
        var inp = qs(f, '[data-upc-pn-input]');
        var v = core.trim(inp ? inp.value : '');
        if (!v) { focusEl(inp); return; }
        if (ctx.mode !== 'finder') return;
        if (inp) inp.value = '';
        if (ctx.noData) return;
        navigate(ctx, { kind: 'pn', pn: v });
      }
    });

    root.addEventListener('input', function (e) {
      var inp = e.target;
      if (inp && inp.hasAttribute && inp.hasAttribute('data-upc-qty-input')) {
        var row = closest(inp, '[data-upc-row]');
        var dec = qs(row, '[data-upc-dec]');
        var n = parseInt(inp.value, 10);
        if (dec && !isNaN(n)) dec.disabled = n <= 1;
      }
    });
    root.addEventListener('change', function (e) {
      var inp = e.target;
      if (inp && inp.hasAttribute && inp.hasAttribute('data-upc-qty-input')) setQty(closest(inp, '[data-upc-row]'), inp.value);
    });
  }

  function boot() {
    qsa(document, '[data-upc]').forEach(function (root) { initRoot(root); });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
